/**
 * ChaptersDialog — gestione dei capitoli della documentazione video.
 *
 * Lista ordinata per `ordine` con: creazione, rinomina inline (titolo +
 * descrizione), attiva/disattiva, riordino con frecce su/giù. Il riordino
 * riscrive `ordine` = indice per TUTTI i capitoli (batch): normalizza anche
 * eventuali buchi o duplicati storici. Scritture Firestore dirette
 * (pattern console super_admin).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Paper, Stack, Switch, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import {
  createChapter,
  reorderChapters,
  updateChapter,
} from '../../services/videoDocsService';
import type { PlatformDocsChapter } from '../../types/videoDocs';

interface Props {
  open: boolean;
  chapters: PlatformDocsChapter[];
  /** Video per capitolo (per mostrare il conteggio accanto al titolo). */
  videoCountByChapter: Record<string, number>;
  onClose: () => void;
  /** Chiamata dopo OGNI scrittura riuscita (il padre ricarica i dati). */
  onChanged: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const ChaptersDialog: React.FC<Props> = ({
  open, chapters, videoCountByChapter, onClose, onChanged, showSnack,
}) => {
  // Draft locali per la rinomina inline (commit su blur / invio)
  const [drafts, setDrafts] = useState<Record<string, { titolo: string; descrizione: string }>>({});
  const [newTitolo, setNewTitolo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset COMPLETO solo all'apertura: durante la sessione i draft non salvati
  // non vanno sovrascritti dal ricaricamento del padre dopo ogni commit.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setError(null);
      setNewTitolo('');
      setDrafts(Object.fromEntries(chapters.map((c) => [
        c.id,
        { titolo: c.titolo, descrizione: c.descrizione ?? '' },
      ])));
    }
    wasOpen.current = open;
  }, [open, chapters]);

  // Capitoli NUOVI arrivati dal ricarico (es. appena creati): aggiungi il
  // draft mancante senza toccare quelli esistenti (in-flight preservati).
  useEffect(() => {
    if (!open) return;
    setDrafts((prev) => {
      const missing = chapters.filter((c) => !prev[c.id]);
      if (missing.length === 0) return prev;
      const next = { ...prev };
      for (const c of missing) {
        next[c.id] = { titolo: c.titolo, descrizione: c.descrizione ?? '' };
      }
      return next;
    });
  }, [open, chapters]);

  const run = async (fn: () => Promise<void>, successMsg: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      showSnack(successMsg, 'success');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    const titolo = newTitolo.trim();
    if (!titolo) {
      setError('Il titolo del capitolo è obbligatorio');
      return;
    }
    run(async () => {
      // In coda alla lista corrente (ordine = numero di capitoli esistenti)
      await createChapter({ titolo, ordine: chapters.length, attivo: true });
      setNewTitolo('');
    }, 'Capitolo creato');
  };

  /** Commit della rinomina se il draft differisce dal valore salvato. */
  const commitRename = (chapter: PlatformDocsChapter) => {
    const draft = drafts[chapter.id];
    if (!draft) return;
    const titolo = draft.titolo.trim();
    const descrizione = draft.descrizione.trim();
    if (!titolo) {
      setDrafts((d) => ({ ...d, [chapter.id]: { ...draft, titolo: chapter.titolo } }));
      setError('Il titolo del capitolo non può essere vuoto');
      return;
    }
    if (titolo === chapter.titolo && descrizione === (chapter.descrizione ?? '')) return;
    run(
      () => updateChapter(chapter.id, { titolo, descrizione: descrizione || undefined }),
      'Capitolo aggiornato',
    );
  };

  const handleToggleAttivo = (chapter: PlatformDocsChapter) => {
    run(
      () => updateChapter(chapter.id, { attivo: !chapter.attivo }),
      chapter.attivo ? 'Capitolo disattivato' : 'Capitolo attivato',
    );
  };

  /** Sposta il capitolo di una posizione e riscrive gli ordini 0..n-1. */
  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= chapters.length) return;
    const ids = chapters.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(() => reorderChapters(ids), 'Ordine capitoli aggiornato');
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
        Capitoli della documentazione
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={1.25}>
          {chapters.map((chapter, index) => {
            const draft = drafts[chapter.id] ?? { titolo: chapter.titolo, descrizione: chapter.descrizione ?? '' };
            const count = videoCountByChapter[chapter.id] ?? 0;
            return (
              <Paper
                key={chapter.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderColor: tokens.color.line,
                  opacity: chapter.attivo ? 1 : 0.65,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  {/* Frecce riordino */}
                  <Stack spacing={0}>
                    <IconButton
                      size="small"
                      disabled={busy || index === 0}
                      onClick={() => handleMove(index, -1)}
                      aria-label="Sposta su"
                    >
                      <ArrowUpward sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={busy || index === chapters.length - 1}
                      onClick={() => handleMove(index, 1)}
                      aria-label="Sposta giù"
                    >
                      <ArrowDownward sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>

                  {/* Rinomina inline */}
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      label="Titolo"
                      value={draft.titolo}
                      disabled={busy}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [chapter.id]: { ...draft, titolo: e.target.value },
                      }))}
                      onBlur={() => commitRename(chapter)}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(chapter); }}
                      fullWidth
                    />
                    <TextField
                      label="Descrizione (facoltativa)"
                      value={draft.descrizione}
                      disabled={busy}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [chapter.id]: { ...draft, descrizione: e.target.value },
                      }))}
                      onBlur={() => commitRename(chapter)}
                      fullWidth
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SoftChip
                        label={count === 1 ? '1 video' : `${count} video`}
                        tone={count > 0 ? 'info' : 'default'}
                        outlined={count === 0}
                      />
                      {!chapter.attivo && <SoftChip label="Disattivo" tone="default" outlined />}
                    </Stack>
                  </Stack>

                  {/* Attiva/disattiva */}
                  <Tooltip title={chapter.attivo ? 'Disattiva capitolo' : 'Attiva capitolo'}>
                    <Switch
                      size="small"
                      checked={chapter.attivo}
                      disabled={busy}
                      onChange={() => handleToggleAttivo(chapter)}
                    />
                  </Tooltip>
                </Stack>
              </Paper>
            );
          })}

          {chapters.length === 0 && (
            <Typography
              sx={{
                fontFamily: tokens.font.display,
                fontStyle: 'italic',
                color: tokens.color.mist,
                textAlign: 'center',
                py: 2,
              }}
            >
              Nessun capitolo: crea il primo qui sotto.
            </Typography>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Creazione nuovo capitolo */}
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Nuovo capitolo"
            placeholder="Titolo del capitolo"
            value={newTitolo}
            disabled={busy}
            onChange={(e) => setNewTitolo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            fullWidth
          />
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Add />}
            disabled={busy || !newTitolo.trim()}
            onClick={handleCreate}
            sx={{ flexShrink: 0 }}
          >
            Crea
          </Button>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
            Le modifiche a titolo e descrizione si salvano uscendo dal campo.
            Un capitolo disattivo non è visibile ai tenant.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};
